import { HttpParams } from '@angular/common/http';
import { Component, EventEmitter, forwardRef, Input, OnDestroy, OnInit, Output, ViewChild, Injector, Type } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, UntypedFormGroup, ValidationErrors, Validator } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Program, ProgramSubmissionType } from '@program/shared/program.model';
import { ProgramService } from '@program/shared/program.service';
import { ProjectPageStatusService } from '@project/shared/project-page-status.service';
import { ProjectType } from '@project/shared/project-type.enum';
import { Project } from '@project/shared/project.model';
import { ProjectService } from '@project/shared/project.service';
import { EventService } from '@shared/event.service';
import { CurrencyExchange } from '@shared/models/currency-exchange/currency-exchange.model';
import { CurrencyExchangeService } from '@shared/models/currency-exchange/currency-exchange.service';
import { REFERENCE_TYPE } from '@shared/reference-types';
import { ReferenceType } from '@shared/references.model';
import { Country } from '@shared/services/country/country.model';
import { CountryService } from '@shared/services/country/country.service';
import { Region } from '@shared/services/region/region.model';
import { RegionService } from '@shared/services/region/region.service';
import { ROLE_TYPE } from '@user/role-type';
import { User } from '@user/user.model';
import { UserService } from '@user/user.service';
import { BsDatepickerConfig } from 'ngx-bootstrap/datepicker';
import { IMultiSelectOption } from 'ngx-bootstrap-multiselect';
import { Subject, zip } from 'rxjs';
import { delay, take, takeUntil } from 'rxjs/operators';

import { FormErrorType } from '../form-error-message/form-error-type.enum';
import { ModalDialogComponent } from '../modal-dialog/modal-dialog.component';
import { isProgramAnswered, NO_PROGRAM, UNCHOSEN_PROGRAM } from './program-selection.constant';
import { controlNameFor, isControlBacked, RuleFieldKey } from './project-type-rules/rule-field-mapping';
import { SpecificationFormRule } from './project-type-rules/specification-form-rule';
import { RuleResolutionContext, WorkflowConfigurationError } from './workflows/project-creation-workflow.model';
import { ExternalPrefillConfirmation, ExternalPrefillProvider } from './workflows/external-prefill-provider.model';
import { joinCommunityNames } from './workflows/vida/vida-workflow.constant';
import { ProjectCreationWorkflowService } from './workflows/project-creation-workflow.service';
import { SpecificationFormService } from './specification-form.service';

/*
 * For create a new project and edit a project
*/
@Component({
  selector: 'oes-project-specification-form',
  templateUrl: './specification-form.component.html',
  styleUrls: ['./specification-form.component.scss'],
  providers: [
    CurrencyExchangeService,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ProjectSpecificationFormComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => ProjectSpecificationFormComponent),
      multi: true
    }
  ]
})
export class ProjectSpecificationFormComponent implements OnInit, OnDestroy, ControlValueAccessor, Validator {
  @ViewChild('exchangeRateModal', {static: false}) exchangeRateModal: ModalDialogComponent;
  @ViewChild('googleMapsModal', {static: false}) googleMapsModal: ModalDialogComponent;
  @ViewChild('typeMismatchModal', {static: false}) typeMismatchModal: ModalDialogComponent;
  @Input() cancelButton = false;
  @Input() project: Project;
  @Input() readOnly = false;
  @Input() submitButtonName = 'save'; // pass to translation "'buttons.' + submitButtonName | translate"
  /**
   * Non-field rendering context (design FR-4). `programLocked` is set by the entry points that
   * create a project from INSIDE a program, where the program is pre-filled and must not change.
   *
   * Without this input plumbed through every host template, `programLocked` is permanently false
   * and the entire locked-program path is dead code.
   */
  @Input() context: RuleResolutionContext = {};
  @Output() saved: EventEmitter<Project> = new EventEmitter<Project>();

  bsConfig: Partial<BsDatepickerConfig> = {
    containerClass: 'theme-default',
    showWeekNumbers: false,
    dateInputFormat: 'YYYY-MM-DD'
  };
  currentCurrency = 'USD';
  dataReady = false;
  errorType = FormErrorType;
  exchangeRates: CurrencyExchange[];
  isCreate: boolean;
  isRequired = false;
  mapOptions: google.maps.MapOptions = {};
  mapType: string;
  markerOption: google.maps.MarkerOptions = {draggable: true};
  markerPosition: google.maps.LatLngLiteral;
  projectTypeTitle = '';
  showTenderProjectField: boolean = false;
  specificationFormGroup: UntypedFormGroup;
  specificationFormRule: SpecificationFormRule;
  /** Set when the program names a workflow this build does not have; blocks the form. */
  workflowConfigurationError: WorkflowConfigurationError;
  /** The selected program's prefill provider instance, or null — which hides the shell. */
  prefillProvider: ExternalPrefillProvider | null = null;
  /** The workflow's read-only card, hosted by the shell via ngComponentOutlet. */
  prefillBlockComponent: Type<unknown> | null = null;
  user: User;
  zoomLevel: number;

  private ngUnsubscribe: Subject<any> = new Subject();
  private programId: string;
  /** Re-entrancy guard for the dependent cascades; see subscribeDependentCascades(). */
  private cascadeInProgress = false;
  /** Set on the first failed submit; drives the consolidated error summary. */
  submitAttempted = false;

  constructor(private _projectService: ProjectService,
              private _programService: ProgramService,
              private _userService: UserService,
              private _countryService: CountryService,
              private _regionService: RegionService,
              private _specificationFormService: SpecificationFormService,
              private _projectPageStatusService: ProjectPageStatusService,
              private _router: Router,
              private _currencyExchangeService: CurrencyExchangeService,
              private _eventService: EventService,
              private _projectCreationWorkflowService: ProjectCreationWorkflowService,
              private _injector: Injector,
              private _translateService : TranslateService){
  }

  ngOnInit() {
    // Reveal gating applies to CREATE only. On edit the project already exists, so everything the
    // resolved rule shows renders immediately — which also avoids gating edge cases for CREDIT and
    // for orgs with no programs at all.
    this.isCreate = !this.project?.id;
    if (this.project?.projectType) {
      this.setSpecificationFormRule(this.project?.projectType);
    }
    this.specificationFormGroup = this._specificationFormService.createForm(this.readOnly, this.specificationFormRule);
    this.specificationFormGroup.controls?.programControl?.setValue(UNCHOSEN_PROGRAM, { emitEvent: false });

    this.subscribeDependentCascades();

    // map
    this.mapSetup();
    this.getOrganizationId();
    this.projectTypeTitle = this.project?.projectType;

    this.specificationFormGroup.valueChanges
    .pipe(takeUntil(this.ngUnsubscribe))
    .subscribe(result => {
      if (this.specificationFormGroup.dirty && this.specificationFormGroup.touched) {
        this._specificationFormService.dirty = true;
      }
      this.setShowTenderProjectField(result?.programControl);
    });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next(null);
    this.ngUnsubscribe.complete();
  }

  private setShowTenderProjectField(programId: string) {

    if (programId !== null) {
      const program = this.programs?.find(p => p.id === programId);
      if (program?.sponsor?.id === this.user?.organization?.id) {
        this.showTenderProjectField = true;
      } else {
        this.showTenderProjectField = false;
      }
    } else {
      this.showTenderProjectField = false;
    }

    if (this.programId !== programId) {
      // The program changed, so the resolved rule may have changed with it. Re-resolve against the
      // newly-selected program and re-shape the group in place. Runs before the tenderProject write
      // below so that write lands on a control the new rule actually keeps.
      const selectedProgram = this.programs?.find(p => p.id === programId) ?? this.project?.program;
      // DT-6472 FR-29: a providesProjectType workflow supplies the type from its provider — any
      // entry-point type is a hint the provider overrides. Clear it (create-mode, unconfirmed
      // only) so the form holds at stage one until confirm; the stage-one overlay then shapes
      // that stage (VIDA: program + slug only, no name field yet).
      if (this.isCreate
          && !this.project?.externalPrefillToken
          && this._projectCreationWorkflowService.providesProjectType(selectedProgram)) {
        // Dev-demo item 6 root cause: this clear ran BEFORE the mismatch comparison could —
        // stash the entry-point choice so the provider's answer can be compared against it.
        if (this.project.projectType) {
          this.entryPointProjectType = this.project.projectType;
        }
        this.project.projectType = null;
      }
      this.bindPrefillProvider(selectedProgram);
      this.reapplyRuleForProgram(selectedProgram);
      if (this.project.tenderProject != null) {
        this.specificationFormGroup?.controls?.tenderProjectControl?.setValue(this.project.tenderProject, { emitEvent: false });
      } else {
        this.specificationFormGroup?.controls?.tenderProjectControl?.setValue(this.showTenderProjectField, { emitEvent: false });
      }
      this.specificationFormGroup.updateValueAndValidity({ emitEvent: false });
      this.programId = programId;
    }
  }

  /**
   * Resolves the field rule through the workflow layer, replacing the old `switch` on project type.
   *
   * The program passed here is the AUTHORITATIVE one — the working `Project`'s. Four sources differ
   * across the entry points, and the dropdown list is NOT one to trust: it arrives at the end of a
   * four-hop async chain, well after `createForm` runs, and `ProgramApplication.program` strips
   * `eligibleProjectTypes`. Resolving from the working project keeps every entry point consistent.
   */
  private setSpecificationFormRule(projectType: ProjectType) {
    const resolved = this._projectCreationWorkflowService.resolve(
      projectType, this.project?.program, this.context);
    this.specificationFormRule = resolved.rule;
    this.workflowConfigurationError = resolved.configurationError;
    this._projectCreationWorkflowService.warnOnBrokenCouplings(projectType, resolved.rule);
  }

  /**
   * Re-resolves and re-applies the rule after the selected program changes.
   *
   * Mutates the existing group in place — the CVA chain's `writeValue` calls `setValue`, which
   * throws on a shape mismatch, so the instance must survive.
   */
  private reapplyRuleForProgram(program: Program) {
    if (!this.specificationFormGroup) {
      return;
    }
    const resolved = this._projectCreationWorkflowService.resolve(
      this.project?.projectType, program, this.context);
    this.specificationFormRule = resolved.rule;
    this.workflowConfigurationError = resolved.configurationError;
    this._projectCreationWorkflowService.warnOnBrokenCouplings(this.project?.projectType, resolved.rule);
    // Controls the user has not interacted with, captured BEFORE the reveal renders. Every one of
    // them is about to be shown for the first time, and their CVAs mark themselves dirty+touched
    // as they initialise — without this, each newly revealed field paints "This is a required
    // field" before the user has looked at it.
    //
    // Keyed on "was clean", NOT on the controls applyRule added: the group is built with all
    // fourteen controls up front and the reveal only un-hides them via *ngIf, so the added list is
    // empty here. Capturing the clean set instead makes it impossible to wipe a real edit made
    // before the reveal (a typed project name, the program selection itself).
    const cleanBeforeReveal = Object.keys(this.specificationFormGroup.controls)
      .filter(name => {
        const control = this.specificationFormGroup.controls[name];
        return !control.touched && !control.dirty;
      });
    this._specificationFormService.applyRule(this.specificationFormGroup, resolved.rule, this.readOnly);
    this.revealedControls = cleanBeforeReveal;
    // One tick is NOT enough: measured in the browser, the dropdowns all flip ~1.1s later, when
    // the cascade below resolves and re-renders them with their options. Clear on the next tick
    // AND again from the cascade's completion.
    this.clearRevealedControlInteraction();
    // Dependent cascades do not fire from applyRule's {emitEvent:false} writes, so drive them
    // explicitly — and only for fields the resolved rule actually shows.
    if (resolved.rule?.fields?.country?.display) { this.onCountryChanged(); }
    if (resolved.rule?.fields?.currency?.display) { this.syncExchangeRateToCurrency(); }
  }

  /**
   * Controls that were clean when the most recent reveal started. Cleared of their CVA-init
   * dirty/touched noise on the next tick and again when a dependent cascade re-renders them.
   *
   * Narrow window: an edit to one of these fields made between the reveal and the cascade landing
   * (~1s, while the field is still rendering) has its touched flag cleared, so its error appears
   * on the next blur rather than immediately. Values are never affected.
   */
  private revealedControls: string[] = [];

  /**
   * Clears the CVA initialisation noise on the controls the reveal just added.
   *
   * Deliberately does NOT reset the group: the program selection that triggered the reveal is a
   * real user edit, and the group must stay dirty so the unsaved-changes guard still fires. Only
   * the newly revealed controls are touched, via `{onlySelf: true}`.
   *
   * A genuine edit to one of these fields re-marks it after the last clear runs, so the user's own
   * interaction is not swallowed.
   */
  private clearRevealedControlInteraction() {
    if (!this.revealedControls.length) {
      return;
    }
    setTimeout(() => this._specificationFormService.clearInitialInteraction(
      this.specificationFormGroup, this.revealedControls), 0);
  }

  /**
   * Marks the freshly-loaded form as untouched by the user. Safe because it runs once, immediately
   * after load, before any interaction is possible.
   */
  private markFormAsLoaded() {
    this.specificationFormGroup.markAsPristine();
    this.specificationFormGroup.markAsUntouched();
    this._specificationFormService.dirty = false;
    this.submitAttempted = false;
  }

  private mapSetup() {
    this.mapOptions.center = {
      lat: this.project.latitude,
      lng: this.project.longitude
    };
    this.mapOptions.zoomControl = true;
    this.mapOptions.zoom = 16;
    this.mapOptions.mapTypeId = 'hybrid';
    this.mapOptions.scaleControl = true;
    this.mapOptions.mapTypeControl = true;
    this.mapOptions.streetViewControl = false;
    this.mapOptions.scrollwheel = false;

    // marker
    this.markerPosition = {
      lat: this.project?.latitude,
      lng: this.project?.longitude
    };
  }

  // map-marker callback
  public addMarker(event) {
    this.markerPosition = event.latLng.toJSON();
    if (this.markerPosition) {
      if (this.markerPosition.lat) {
        this.specificationFormGroup.controls['latitudeControl'].setValue(this.markerPosition.lat, {emitEvent: false});
      }
      if (this.markerPosition.lng) {
        this.specificationFormGroup.controls['longitudeControl'].setValue(this.markerPosition.lng, {emitEvent: false});
      }
      this.specificationFormGroup.markAsDirty();
      this.specificationFormGroup.markAsTouched();
    }
  }

  private getOrganizationId() {
    this._userService.getCurrentUser()
    .pipe(takeUntil(this.ngUnsubscribe))
    .subscribe((user: User) => {
      if (user) {
        this.user = user;
        this.getSelectOptionItems();
      }
    });
  }

  private getSelectOptionItems() {
    if (this._userService.getSelectedRole() === ROLE_TYPE.DEVELOPER_USER) {
      this.getDeveloperPrograms();
    } else if (this._userService.getSelectedRole() === ROLE_TYPE.FINANCE_USER) {
      this.getInvestorPrograms();
    }
    // this.getStatusTypes();
    // this.getCurrencyTypes();
    // this.getProjectTypes(); // NOTE: Need for create a project
    // this.getCountries();
  }

  // Investor: for tendered project
  private getInvestorPrograms() {
    this._programService.getPrograms()
    .pipe(take(1))
    .subscribe((programs: Program[]) => {
      const filteredPrograms = this.filterProgramsForProjectType(programs);
      this._specificationFormService.programs = this.sortProgramsCaseInsensitive(filteredPrograms);
      this.getOtherOptionItems();
    });
  }

  // Developer
  private getDeveloperPrograms() {
    this._programService.getApproved()
    .pipe(take(1))
    .subscribe((approvedPrograms: Program[]) => {
      const filteredPrograms = this.filterProgramsForProjectType(approvedPrograms);
      this._specificationFormService.programs = this.sortProgramsCaseInsensitive(filteredPrograms);
      this.getOtherOptionItems();
    });
  }

  /**
   * Filters the program list for the current project type.
   *
   * WHEN THE TYPE IS UNKNOWN THE LIST IS RETURNED UNFILTERED. The previous developer-side code was
   * `let filtered = []; if (ci) {...} else if (projectType) {...}` with NO else, so a deferred or
   * absent type produced an EMPTY list — which hides the program field, makes the form think there
   * is nothing to answer, and lets the project save with no program at all. The investor side had
   * the same shape via `includes(undefined)`, false for every program that declares types.
   *
   * Eligibility is still enforced: it becomes a validation once the type is known (and the server
   * enforces it regardless), rather than a filter that silently empties the list beforehand.
   */
  private filterProgramsForProjectType(programs: Program[]): Program[] {
    if (!this.project?.projectType) {
      return programs ?? [];
    }
    if (this.project.projectType === ProjectType.ci) {
      return programs.filter(program => program.submissionType === ProgramSubmissionType.C_I);
    }
    return programs.filter(program => {
      if (program.submissionType !== ProgramSubmissionType.PROJECT) {
        return false;
      }
      // An empty eligible list means all types are allowed.
      if (program.eligibleProjectTypes && program.eligibleProjectTypes.length > 0) {
        return program.eligibleProjectTypes.includes(this.project?.projectType);
      }
      return true;
    });
  }

  private sortProgramsCaseInsensitive(programs: Program[]): Program[] {
    return programs.sort((a, b) => {
      return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
    });
  }

  /**
   * Stage two of the create reveal (design FR-5).
   *
   * Gates only the RULE-DRIVEN sections. Cancel/Create and the error summary stay outside it, per
   * the mockup — they are the only path by which a "Program is required" message can ever appear.
   *
   * Reads `controls.programControl?.value`, never the group-level value: a disabled control is
   * excluded from `FormGroup.value`, so the group-level read would report undefined for a locked
   * program and hold the form at stage one forever.
   */
  get showRuleDrivenSections(): boolean {
    if (!this.isCreate) {
      return true;
    }
    // No program field at all (no eligible programs, or the rule hides it) => nothing to answer.
    if (!this.specificationFormRule?.fields?.program?.display || !(this.programs?.length > 0)) {
      return true;
    }
    if (this.context?.programLocked) {
      return true;
    }
    if (!isProgramAnswered(this.specificationFormGroup?.controls?.programControl?.value)) {
      return false;
    }
    // A workflow that supplies the project type holds stage two until its provider confirms.
    return !!this.project?.projectType;
  }

  /** True while the program question is unanswered — drives the empty-state card. */
  get showSelectProgramEmptyState(): boolean {
    return this.isCreate
      && this.specificationFormRule?.fields?.program?.display
      && this.programs?.length > 0
      && !this.context?.programLocked
      && !isProgramAnswered(this.specificationFormGroup?.controls?.programControl?.value);
  }

  /** Placeholder styling must test the sentinel, not `!value` — NO_PROGRAM is a real answer. */
  get programPlaceholderActive(): boolean {
    return !isProgramAnswered(this.specificationFormGroup?.controls?.programControl?.value);
  }

  /**
   * Name of the currently-selected program, for the pill above the rule-driven fields.
   *
   * Resolved from the dropdown list first, then from the working `Project` — a locked program is
   * not guaranteed to survive the `getApproved()` + submission-type + eligible-type filter, and the
   * pill must not blank out when it does not.
   */
  get selectedProgramName(): string {
    const selected = this.specificationFormGroup?.controls?.programControl?.value;
    if (!isProgramAnswered(selected) || selected === NO_PROGRAM) {
      return '';
    }
    return this.programs?.find(program => program.id === selected)?.name
      ?? (this.project?.program?.id === selected ? this.project?.program?.name : '')
      ?? '';
  }

  /**
   * The pill names a real program, so it is hidden for "No Program" and before an answer.
   *
   * CREATE ONLY. It is a reveal affordance — it answers "you picked a program, here is what it
   * asks for", and its note reads "Additional details required for this program". On Project ->
   * Information nothing is being revealed: every field is shown from the outset, the program
   * dropdown directly above already names the program, and the note describes a step that is not
   * happening. Ungated, the edit screen would show create-flow copy on a saved project.
   */
  get showProgramPill(): boolean {
    return this.isCreate && this.showRuleDrivenSections && !!this.selectedProgramName;
  }

  /**
   * Consolidated list of missing required fields, for the summary strip above the buttons.
   *
   * Sourced from the INNER `specificationFormGroup`, never the outer CVA group: the outer one lags
   * two macrotasks behind (`delay(0)` at each of the two CVA layers), so a summary driven from it
   * would report the previous keystroke's state.
   *
   * Only reports fields the resolved rule actually shows, so a hidden-but-invalid control cannot
   * produce an error the user has no way to fix.
   */
  get missingRequiredFieldLabels(): string[] {
    const fields = this.specificationFormRule?.fields;
    if (!fields || !this.specificationFormGroup) {
      return [];
    }
    return Object.keys(fields)
      .filter(key => fields[key]?.display && fields[key]?.required && isControlBacked(key))
      // Stage one renders only name + program; reporting Country/City/COD there would name fields
      // the user cannot see, let alone fix.
      .filter(key => this.showRuleDrivenSections || key === 'name' || key === 'program')
      .filter(key => {
        const control = this.specificationFormGroup.controls[controlNameFor(key as RuleFieldKey)];
        // Disabled controls are excluded from validation, which is correct here: a fixedValue or
        // provider-owned field is not something the user can be asked to fill in.
        return control && control.enabled && control.invalid;
      })
      .map(key => {
        const headingKey = fields[key]?.headingKey ?? this.defaultHeadingKeyFor(key);
        return this._translateService.instant(`project.create.${headingKey}`);
      });
  }

  /**
   * Shown only after a submit ATTEMPT, never merely because the group is touched.
   *
   * `oes-custom-dropdown`'s CVA calls `onTouched()`/`onChange()` during initialization, so the
   * group arrives touched AND dirty on a freshly-loaded form — gating on `touched` would show a
   * five-field error strip before the user typed anything. This mirrors the mockup, which sets its
   * `touched` flag inside the Create handler.
   */
  get showErrorSummary(): boolean {
    return this.submitAttempted && this.missingRequiredFieldLabels.length > 0;
  }

  get errorSummaryText(): string {
    const missing = this.missingRequiredFieldLabels;
    if (missing.length === 1) {
      return this._translateService.instant('project.create.error-summary-single', { field: missing[0] });
    }
    return this._translateService.instant('project.create.error-summary-multi',
      { count: missing.length, fields: missing.join(', ') });
  }

  /** Rule keys whose label lives under a differently-named i18n key. */
  private defaultHeadingKeyFor(key: string): string {
    const overrides = {
      state: 'region', currency: 'tariff-currency', projectType: 'type',
      address1: 'address-1', address2: 'address-2', postalCode: 'postal-code',
      exchangeRate: 'exchange-rate', tenderProject: 'tender-project'
    };
    return overrides[key] ?? key;
  }

  // --- oes-custom-dropdown option lists -------------------------------------------------------
  //
  // The component takes IMultiSelectOption[] ({ id, name, disabled? }) rather than <option>
  // children. It has no (change) output, so the country->region and currency->exchangeRate
  // cascades move to valueChanges subscriptions; see subscribeDependentCascades().
  //
  // No "-- select --" entry is added: `placeholderKey` renders that, and an explicit empty option
  // would be selectable and would read as an answer.

  get programOptions(): IMultiSelectOption[] {
    const expiredSuffix = (program: Program) => this.expiredProgram(program.endDateTime)
      ? ` (${this._translateService.instant(this.submittedToProgram(program.id)
          ? 'project.create.submitted' : 'project.create.past-submission-date')})`
      : '';
    return [
      { id: NO_PROGRAM, name: this._translateService.instant('project.create.no-program') },
      ...(this.programs ?? []).map(program => ({
        id: program.id,
        name: `${program.name}${expiredSuffix(program)}`,
        // Expired programs stay visible but unselectable, as they were with [disabled] on <option>.
        disabled: this.expiredProgram(program.endDateTime)
      }))
    ];
  }

  get statusOptions(): IMultiSelectOption[] {
    return (this.projectStatusTypes ?? []).map(type => ({
      id: type.id, name: this._translateService.instant(type.code)
    }));
  }

  get countryOptions(): IMultiSelectOption[] {
    return (this.countries ?? []).map(country => ({ id: country.id, name: country.name }));
  }

  get regionOptions(): IMultiSelectOption[] {
    return (this.regions ?? []).map(region => ({ id: region.id, name: region.name }));
  }

  get currencyOptions(): IMultiSelectOption[] {
    return (this.currencyTypes ?? []).map(type => ({
      id: type.id, name: this._translateService.instant('currency-type.' + type.id)
    }));
  }

  /**
   * Drives the dependent cascades from control changes.
   *
   * `oes-custom-dropdown` exposes no `(change)` output, so migrating away from native `<select>`
   * drops the template handlers that used to fire these. Without this, picking a country would
   * never reload the State list — `onCountryChanged` is the only caller of `getRegions`.
   *
   * `applyRule` and every programmatic write use `{emitEvent: false}`, so this subscription sees
   * USER changes only; the programmatic path is driven directly from `reapplyRuleForProgram`. The
   * guard is belt-and-braces against a cascade writing a value that re-enters its own handler.
   */
  private subscribeDependentCascades() {
    this.specificationFormGroup.controls['countryControl']?.valueChanges
    .pipe(takeUntil(this.ngUnsubscribe))
    .subscribe(() => {
      if (this.cascadeInProgress) { return; }
      this.cascadeInProgress = true;
      try { this.onCountryChanged(); } finally { this.cascadeInProgress = false; }
    });

    this.specificationFormGroup.controls['currencyControl']?.valueChanges
    .pipe(takeUntil(this.ngUnsubscribe))
    .subscribe(() => {
      if (this.cascadeInProgress) { return; }
      // A re-emission of the value already in force is not a currency change, and onCurrencyChanged
      // blanks the exchange rate. The host's `setDisabledState` calls `enable()` on the whole
      // group, which re-emits every control — without this a saved NGN rate was wiped on load
      // (DT-6510).
      if (this.specificationFormGroup.controls['currencyControl'].value === this.currentCurrency) { return; }
      this.cascadeInProgress = true;
      try { this.onCurrencyChanged(); } finally { this.cascadeInProgress = false; }
    });
  }

  expiredProgram(endDateTime: Date) {
    return new Date(endDateTime) < new Date();
  }

  submittedToProgram(programId: string) {
    if (this.project.program.id === programId &&
       (this.project.projectPermissions?.portfolio?.submittedToProgram ||
        this.project.projectPermissions?.project?.submittedToProgram)) {
      return true;
    }
    return false;
  }

  private getOtherOptionItems() {
    // PROJECT_TYPE was dropped here: Asset Type renders as a locked display of
    // project.projectType and no longer needs the reference list.
    //
    // NOTE the consumers below are POSITIONAL. Removing an observable without renumbering them
    // leaves countries unset, getFormValues then resolves no country, and every save fails
    // server-side with "Country cannot be empty" — a failure the milestone's own success criteria
    // would not have caught.
    zip(
      this._projectService.getReferenceType(REFERENCE_TYPE.PROJECT_STATUS_TYPE),
      this._projectService.getReferenceType(REFERENCE_TYPE.CURRENCY_TYPE),
      this._countryService.getCountries()
    ).subscribe(results => {
      if (results && results.length > 0) {
        if (results[0] && results[0].length > 0) {
          this._specificationFormService.projectStatusTypes = results[0];
        }
        if (results[1] && results[1].length > 0) {
          this._specificationFormService.currencyTypes = results[1];
        }
        if (results[2] && results[2].length > 0) {
          this._specificationFormService.countries = results[2];
        }
      }
      this.setupData();
    });
  }

  private setupData() {
    if (this.project) {
      if (this.project.country?.id) {
        // To set state/province
        this.onCountryChanged(this.project.country?.id);
      }
      this._specificationFormService.setFormValues(this.specificationFormGroup, this.project, this.project.cod, this.readOnly, this.specificationFormRule);
      // setFormValues writes the stored currency with {emitEvent: false}, so record it here or the
      // currency subscription would read the stored value as a user change.
      this.currentCurrency = this.specificationFormGroup.controls['currencyControl']?.value ?? this.currentCurrency;

      this.dataReady = true;

      // Clear the load-time noise AFTER the view has rendered the dropdowns.
      //
      // oes-custom-dropdown's CVA calls onChange()/onTouched() while initializing, which leaves the
      // group dirty AND touched before the user has done anything. The reset has to run after
      // `dataReady` renders them — doing it here, synchronously, would be undone moments later.
      // Consequences if skipped: the error summary appears on load, and on Project -> Information
      // the unsaved-changes dialog fires when merely opening the page.
      setTimeout(() => this.markFormAsLoaded(), 0);
    }
  }

  // callback: county select option
  public onCountryChanged(countryId?: string) {
    // Guarded: countryControl does not exist when the resolved rule hides country — including
    // STAGE_ONE_RULE, which hides everything but name and program. This read happens BEFORE the
    // state?.display check below, so it must not assume the control is there.
    const id = countryId ? countryId : this.specificationFormGroup.controls.countryControl?.value;
    if (id && id !== '' && this.specificationFormRule?.fields?.state?.display) {
      let params = new HttpParams()
        .append('countryId', id);
      // don't show deleted regions for a new project. Existing projects may have a soft-deleted region
      if (!this.project.id) {
        params = params.append('includeDeleted', false);
      }
      const option = {
        params: params
      };
      this._regionService.getRegions(option)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((regions: Region[]) => {
        this._specificationFormService.regions = regions;
        const regionControl = this.specificationFormGroup.controls['regionControl'];
        if (!regionControl) {
          return;
        }
        const stateField = this.specificationFormRule?.fields?.state;
        if (regions.length === 0) {
          regionControl.setValue('', {emitEvent: false});
          regionControl.disable({emitEvent: false});
          this.isRequired = false;
        } else {
          // force to set a region because it's required field and I couldn't find to changed the form validation type
          // Null-guarded: project.region is absent on a new project. Skipped entirely when the field
          // is read-only or provider-owned — force-selecting regions[0] there would overwrite a
          // provider-supplied value, and the {emitEvent: true} would re-enter the cascade.
          const ruleOwnsTheValue = stateField?.readOnly || stateField?.fixedValue !== undefined;
          if (!ruleOwnsTheValue &&
              !this._specificationFormService.regions.map(reg => reg.id).includes(this.project.region?.id)) {
            regionControl.setValue(this.regions[0].id, {emitEvent: false});
          }
          if (ruleOwnsTheValue) {
            // applyFieldStates disabled this control for the rule; loading the options list
            // must not undo that — re-assert rather than enable (DT-6472: provider-owned state).
            regionControl.disable({emitEvent: false});
          } else {
            regionControl.enable({emitEvent: false});
          }
          this.isRequired = true;
        }

        if (this.readOnly) { regionControl.disable({emitEvent: false}); }

        // Re-render with the loaded regions re-initialises the reveal's dropdown CVAs, which marks
        // them dirty+touched again. Clear that here, after the render this response causes.
        this.clearRevealedControlInteraction();
      });
    }
  }

  // callback: currency select option
  public onCurrencyChanged() {
    // GUARDED. exchangeRateControl does not always exist even without any workflow: CiSiteRule
    // ships currency.display true with exchangeRate.display false (same for cookstove, credit, pue
    // and shs), and createForm only adds the control when exchangeRate.display is true. An
    // unguarded dereference here throws on every C&I create and every C&I Information load.
    this.currentCurrency = this.specificationFormGroup.controls.currencyControl?.value;
    const exchangeRateControl = this.specificationFormGroup.controls['exchangeRateControl'];
    if (!exchangeRateControl) {
      return;
    }
    if (this.currentCurrency && this.currentCurrency === 'USD') {
      exchangeRateControl.setValue(1, {emitEvent: false});
      exchangeRateControl.disable({emitEvent: false});
    } else {
      exchangeRateControl.setValue('', {emitEvent: false});
      exchangeRateControl.enable({emitEvent: false});
    }
  }

  /**
   * Re-aligns the exchange rate with the CURRENT currency without discarding a stored rate (DT-6510).
   *
   * `onCurrencyChanged` blanks the rate for a non-USD currency, which is right when the user picks a
   * new currency but wrong when the rule is merely re-applied. `reapplyRuleForProgram` runs on the
   * first form change of every saved project (`programId` starts undefined), so routing it through
   * `onCurrencyChanged` wiped a saved NGN rate the moment the user touched the Information page.
   *
   * Only a rate sitting in the USD lock (disabled, value 1) is cleared, when the currency has since
   * become non-USD. A control the form is read-only or the rule owns stays as applyRule left it.
   */
  private syncExchangeRateToCurrency() {
    this.currentCurrency = this.specificationFormGroup.controls.currencyControl?.value;
    const exchangeRateControl = this.specificationFormGroup.controls['exchangeRateControl'];
    if (!exchangeRateControl) {
      return;
    }
    if (this.currentCurrency && this.currentCurrency === 'USD') {
      exchangeRateControl.setValue(1, {emitEvent: false});
      exchangeRateControl.disable({emitEvent: false});
      return;
    }
    const exchangeRateField = this.specificationFormRule?.fields?.exchangeRate;
    const ruleOwnsTheValue = exchangeRateField?.readOnly || exchangeRateField?.fixedValue !== undefined;
    if (this.readOnly || ruleOwnsTheValue || exchangeRateControl.enabled) {
      return;
    }
    exchangeRateControl.setValue('', {emitEvent: false});
    exchangeRateControl.enable({emitEvent: false});
  }

  // callback: View Rates link
  public showExchangeRates() {
    let params = new HttpParams();
    // Guarded for the same reason as onCurrencyChanged: the pair is not always present together.
    const currencyId = this.specificationFormGroup.controls['currencyControl']?.value;
    if (currencyId) {
      params = params.set('code', currencyId);
    }
    this._currencyExchangeService.list(params)
    .pipe(takeUntil(this.ngUnsubscribe))
    .subscribe((exchangeRateResults: CurrencyExchange[]) => {
      this.exchangeRates = exchangeRateResults;
      if (this.exchangeRates?.length > 0) {
        this.exchangeRateModal.show();
      }
    });
  }

  public onSubmit() {
    if (this.specificationFormGroup.invalid) {
      this.submitAttempted = true;
      this.specificationFormGroup.markAllAsTouched();
      return;
    }
    const oldProgram = this.project.program;
    const updatedProject = this._specificationFormService.getFormValues(this.specificationFormGroup, this.specificationFormRule, this.project);

    // A LOCKED program is resolved from the working project, never from the dropdown list.
    // getFormValues looks the program up in `_programs` with no fallback, and that list is filtered
    // by getApproved() + submission type + eligible types — so a program that fails the filter
    // resolves to undefined. On edit that reads as a program CHANGE and routes a Notes-only save
    // through updateWithSideEffects, which soft-deletes the sponsor ProjectConnection and resets
    // applicationFormComplete. Silent data loss for someone who only edited a description.
    if (this.context?.programLocked && this.project.program?.id) {
      updatedProject.program = this.project.program;
    }

    // A program-resolution FAILURE must abort, never be interpreted as "the user chose No Program".
    const programWasExpected = !!oldProgram?.id;
    const programWentMissing = programWasExpected && !updatedProject.program?.id;
    if (programWentMissing && this.context?.programLocked) {
      this._eventService.error(this._translateService.instant('form.error.fill-required'));
      return;
    }

    if (this.project.id && oldProgram?.id !== updatedProject.program?.id) {
       this._projectService.updateWithSideEffects(updatedProject, true)
        .pipe(take(1))
        .subscribe(projectWithSideEffects => {
          const project = projectWithSideEffects.project;
          if (projectWithSideEffects.sideEffects?.projectConnectionSoftDeleted) {
            this.notifyProjectUnsharedWithPreviousProgramSponsor(project);
          }
          this.afterProjectSaved(project);
        });
    } else {
      this._projectService.createUpdate(updatedProject, true)
        .pipe(take(1))
        .subscribe(project => {
          this.afterProjectSaved(project);
        });
    }
  }

  private afterProjectSaved(project: Project) {
    if (project) {
      this.specificationFormGroup.markAsPristine();
      this.specificationFormGroup.markAsUntouched();
      this.project = project;
      this.saved.emit(project);
      if (project.projectType !== ProjectType.ci) {
        this._projectPageStatusService.getFinancialModel(project.id);
      }
    }
  }

  private notifyProjectUnsharedWithPreviousProgramSponsor(project: Project) {
    this._eventService.success(
        this._translateService.instant('success-message.project-unshared-with') + ' '
        + project.program.sponsor.name + '.'
        + this._translateService.instant('success-message.project-reshare-advice'));
  }

  // to grey out a default value
  public isDefaultValue(controlName: string) {
    if (controlName && controlName !== '') {
      // Guarded: called from three template bindings, which re-evaluate during the window where
      // applyRule has removed a control but the rule reference has not been reassigned yet. This
      // was the only unguarded control dereference in the template.
      const value = this.specificationFormGroup.controls[controlName]?.value;
      return {'select-w-placeholder' : value === undefined};
    }
  }

  // cancel button
  public cancel() {
    this.specificationFormGroup.markAsPristine();
    this._router.navigate(['/oes/projects']);
  }

  checkLocation() {
    this.googleMapsModal.show();
  }

  // access from html
  get projectStatusTypes(): ReferenceType[] {
    return this._specificationFormService.projectStatusTypes;
  }
  get countries(): Country[] {
    return this._specificationFormService.countries;
  }
  get regions(): Region[] {
    return this._specificationFormService.regions;
  }
  get programs(): Program[] {
    return this._specificationFormService.programs;
  }
  get currencyTypes(): ReferenceType[] {
    return this._specificationFormService.currencyTypes;
  }

  public onTouched: () => void = () => {};

  writeValue(val: any): void {
    if (val) {
      this.specificationFormGroup.setValue(val, {emitEvent: false});
    }
  }

  registerOnChange(fn: any): void {
    this.specificationFormGroup.valueChanges.pipe(
      // prevent Previous value: 'ng-pristine: true'. Current value: 'ng-pristine: false' error
      delay(0)
    ).subscribe(fn);
  }

  registerOnTouched(fn: any): void {
    this.specificationFormGroup.valueChanges.subscribe(fn);
  }

  setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.specificationFormGroup.disable() : this.specificationFormGroup.enable();
  }

  validate(c: AbstractControl): ValidationErrors | null {
    return this.specificationFormGroup.valid ? null : {invalidForm: {valid: false, message: 'Invalid'}};
  }

  /** Resolves the selected program's provider Type into an instance; null hides the shell. */
  private bindPrefillProvider(program: Program) {
    const providerType = this._projectCreationWorkflowService.prefillProviderTypeFor(program);
    this.prefillProvider = providerType ? this._injector.get(providerType) : null;
    this.prefillBlockComponent =
      (this._projectCreationWorkflowService.workflowFor(program)?.blockComponent as
        Type<unknown>) ?? null;
  }

  /** The program id the confirm endpoint is scoped to. */
  get selectedProgramIdForPrefill(): string {
    const selected = this.specificationFormGroup?.controls?.programControl?.value;
    return typeof selected === 'string' ? selected : '';
  }

  /**
   * Stage one is unfinished: a provider is bound, nothing has been confirmed, and the type the
   * provider supplies is still null. The external id lives in the prefill shell, NOT in the form
   * group, so `specificationFormGroup.valid` cannot see that it is empty — selecting a program is
   * enough to satisfy the group and enable Create (DEF-VDA-001). Creating here would post a
   * project with no type and no link.
   */
  get awaitingPrefillConfirmation(): boolean {
    return this.isCreate
      && !!this.prefillProvider
      && !this.project?.externalPrefillToken
      && !this.project?.projectType;
  }

  /** True while the create flow should show the prefill shell (DT-6472 FR-29 stage one). */
  get showPrefillShell(): boolean {
    return this.isCreate
      && !!this.prefillProvider
      // A program that cannot honor the workflow must not offer its entry point: linking would
      // succeed and then prefill nothing, which is the failure this error exists to explain.
      && !this.workflowConfigurationError
      && isProgramAnswered(this.specificationFormGroup?.controls?.programControl?.value)
      && this.specificationFormGroup?.controls?.programControl?.value !== NO_PROGRAM;
  }

  /**
   * DT-6472 FR-29: a confirmed link supplies the type (revealing stage two), the save token, and
   * the prefilled-but-editable name — one community's name verbatim, several joined "A, B and C".
   * The patch is presentational; the server re-derives from its stored record on save.
   */
  /** Dev-demo item 6: the provider returned a different technology type than the entry point. */
  typeMismatch: { chosen: ProjectType; returned: ProjectType } | null = null;
  /** The type picked on the claims entry point, stashed before the stage-one clear nulls it. */
  entryPointProjectType: ProjectType | null = null;

  onPrefillConfirmed(confirmation: ExternalPrefillConfirmation) {
    this.project.externalPrefillToken = confirmation.token;
    this.project.workflowPayload = confirmation.workflowPayload;
    const patch = confirmation.patch ?? {};
    if (typeof patch['projectType'] === 'string') {
      // Dev-demo item 6: the provider's type WINS (unchanged), but when it differs from the
      // type the developer picked at the entry point, say so — a silent swap read as a bug.
      const chosenType = this.project.projectType ?? this.entryPointProjectType;
      const returnedType = patch['projectType'] as ProjectType;
      if (chosenType && chosenType !== returnedType) {
        this.typeMismatch = { chosen: chosenType, returned: returnedType };
        setTimeout(() => this.typeMismatchModal?.show());
      }
      this.project.projectType = returnedType;
    }
    const selectedProgram = this.programs?.find(p => p.id === this.selectedProgramIdForPrefill)
      ?? this.project?.program;
    this.reapplyRuleForProgram(selectedProgram);
    const communityNames = joinCommunityNames(patch['communityNames'] as string[]);
    const nameControl = this.specificationFormGroup?.controls?.nameControl;
    if (nameControl) {
      // REA alignment (2026-09-01): names are STANDARDIZED — the derived name is prefilled and
      // the field locks. (Previously prefilled-but-editable; the dirty guard went with the
      // editability.)
      //
      // Only the VALUE is set here. The LOCK is `VIDA_WORKFLOW.ruleOverlay.name.readOnly`, applied
      // by the `reapplyRuleForProgram` call above (and by `setFormValues` when a saved project is
      // reopened). Disabling it a second time here is what hid the missing overlay entry until QA:
      // create looked right while edit came back editable. One mechanism owns the lock.
      nameControl.setValue(communityNames, { emitEvent: false });
    }
    // City takes the SAME joined community names as the name (owner ruling 2026-09-24,
    // superseding the 2026-09-11 city-from-LGA ruling). Joined here rather than server-side so
    // one implementation of the "A, B and C" format serves both fields. Left EDITABLE: unlike the
    // name it is not an REA-standardized fact, and on a multi-community project a developer may
    // well want something shorter than the full list.
    const cityControl = this.specificationFormGroup?.controls?.cityControl;
    if (cityControl && communityNames) {
      cityControl.setValue(communityNames, { emitEvent: false });
    }
    // Presentational echoes for the read-only provider-owned fields. The state lands as
    // `regionId` (the backend resolves the VIDA state string to a Region row at confirm) and the
    // form's control is `regionControl`, holding a region id — its options arrive async from the
    // country cascade, which is fine: the control value is set first and the dropdown renders the
    // name once the list loads.
    [['latitude', 'latitudeControl'], ['longitude', 'longitudeControl'],
      ['regionId', 'regionControl']]
      .forEach(([key, controlName]) => {
        const control = this.specificationFormGroup?.controls?.[controlName];
        if (control && patch[key] !== undefined && patch[key] !== null) {
          control.setValue(patch[key], { emitEvent: false });
        }
      });
  }

  /** Discarding the link reverts to the stage-one hold and drops the save token. */
  onPrefillCleared() {
    // The lock belongs to the confirmed link — clearing it returns a normal editable field.
    this.specificationFormGroup?.controls?.nameControl?.enable({ emitEvent: false });
    this.project.externalPrefillToken = null;
    this.project.workflowPayload = null;
    this.project.projectType = null;
    const selectedProgram = this.programs?.find(p => p.id === this.selectedProgramIdForPrefill)
      ?? this.project?.program;
    this.reapplyRuleForProgram(selectedProgram);
  }
}
